import cx from 'clsx';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

/**
 * Public site key of the placekit.io Turnstile widget, injected at build time.
 * Empty on local dev and on any build without the secret: the widget is then
 * skipped entirely and forms keep working exactly as they did before.
 */
export const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

/** Whether a site key is configured, i.e. whether forms must fetch a token. */
export const turnstileEnabled = Boolean(turnstileSiteKey);

/** How long a submit waits for the challenge to resolve. */
const TOKEN_TIMEOUT = 15000;

// Cloudflare's own failure reasons are raw numeric codes, so they only ever go
// to the console, never to a visitor.
const warn = (reason) => (detail) => {
  console.warn(`[turnstile] ${reason}`, detail ?? '');
};

/**
 * Read the token of a mounted `TurnstileField` at submit time, so there is a
 * single source of truth and no stale token in React state.
 *
 * Resolves to `undefined` when no site key is configured, and also when the
 * challenge cannot be read at all (Cloudflare down, script blocked, widget
 * never mounted): the form then posts without a token and the server decides.
 * It fails open when Cloudflare itself is unreachable and answers 403 only when
 * Cloudflare is up and the token is missing or invalid, which surfaces through
 * the error path each form already has. A visitor is never blocked by our own
 * anti-bot check.
 * @arg {object} ref Ref passed to `TurnstileField`.
 * @return {Promise<string|undefined>}
 */
export const getTurnstileToken = async (ref) => {
  if (!turnstileEnabled) {
    return undefined;
  }
  try {
    return await ref.current.getResponsePromise(TOKEN_TIMEOUT);
  } catch (err) {
    warn('could not read the token, submitting without one')(err);
    return undefined;
  }
};

/**
 * Cloudflare Turnstile widget for our forms.
 *
 * Rendered with `appearance: 'interaction-only'`, so it stays invisible and
 * zero-height unless Cloudflare decides a human interaction is needed. Mount it
 * inside a container that adds no spacing of its own (no flex or grid gap), so
 * the invisible state costs exactly zero pixel.
 *
 * The ref is the library's own widget instance: `getTurnstileToken(ref)` reads
 * the token and `ref.current.reset()` asks for a fresh one (tokens are single
 * use, so reset after every submit).
 */
const TurnstileField = forwardRef(({ className, id }, ref) => {
  if (!turnstileEnabled) {
    return null;
  }

  return (
    // `size: 'flexible'` sets an inline `min-width: 300px` on the widget
    // container, which would widen a narrow card even while invisible.
    <div className={cx('[&>div]:w-full! [&>div]:min-w-0!', className)}>
      <Turnstile
        ref={ref}
        id={id}
        siteKey={turnstileSiteKey}
        options={{
          appearance: 'interaction-only',
          size: 'flexible',
          theme: 'light',
          // The token travels in the request body, no hidden input needed.
          responseField: false,
        }}
        onError={warn('challenge error')}
        onTimeout={warn('challenge timed out')}
        onUnsupported={warn('unsupported browser')}
      />
    </div>
  );
});

TurnstileField.displayName = 'TurnstileField';

TurnstileField.propTypes = {
  /** Classes applied to the widget container. */
  className: PropTypes.string,
  /** Widget container id, unique per instance. */
  id: PropTypes.string.isRequired,
};

export default TurnstileField;
